<template>
    <div class="w-full text-slate-800 dark:text-slate-100 font-normal">
        <p
            v-if="(displayedCard || selectedCard?.card) && !changeCard"
            class="mb-2 w-full text-sm font-medium flex justify-center items-center gap-3 text-center"
        >
            <label class="flex gap-1 cursor-pointer items-center ml-2">
                <WalletIcon icon="arrow-redo" class="w-4 h-4" />
                Change card
                <input
                    type="checkbox"
                    class="hidden"
                    :checked="changeCard"
                    @change="$emit('update:changeCard', true)"
                    value="1"
                />
            </label>
        </p>
        <Transition name="fade">
            <div
                class="flex flex-col gap-3 w-full min-w-80 mx-auto mb-3 mt-5"
                v-if="changeCard || !selectedCard?.card"
            >
                <div class="flex items-center w-full">
                    <div class="flex-1 border-t border-slate-200 dark:border-slate-700"></div>
                    <span class="px-4 text-sm font-medium text-slate-500 dark:text-slate-400">
                        Change card to
                    </span>
                    <div class="flex-1 border-t border-slate-200 dark:border-slate-700"></div>
                </div>

                <div class="flex items-end gap-2 w-full">
                    <div v-if="changeCard" class="relative w-full">
                        <div class="relative w-full">
                            <img
                                v-if="!hasSelectedCard || debugCardQuery"
                                src="/images/placeholder-search-card-input.png"
                                alt="Card placeholder"
                                class="absolute left-3 top-1/2 -translate-y-1/2 w-7 h-6 object-contain pointer-events-none z-10"
                            />
                            <input
                                :ref="(el) => $emit('update:debugInputRef', el)"
                                type="text"
                                class="h-10 w-full flex items-center gap-2 text-left pr-10 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 py-1.5 placeholder:text-slate-500 dark:placeholder-slate-500 placeholder:text-sm transition-colors text-sm focus:ring-0 focus:border-slate-800 dark:focus:border-slate-500 hover:border-slate-800 dark:hover:border-slate-500"
                                :class="[
                                    hasSelectedCard && !debugCardQuery
                                        ? 'text-transparent caret-slate-500'
                                        : 'text-slate-800 dark:text-slate-100',
                                    !hasSelectedCard || debugCardQuery
                                        ? 'pl-12'
                                        : 'pl-3',
                                ]"
                                :value="debugCardQuery"
                                @input="
                                    $emit(
                                        'update:debugCardQuery',
                                        $event.target.value
                                    );
                                    $emit('search');
                                "
                                :placeholder="
                                    hasSelectedCard
                                        ? ''
                                        : 'Type card name or issuing bank'
                                "
                                @focus="$emit('focus')"
                                autocomplete="off"
                            />

                            <!-- Selected card overlay -->
                            <span
                                v-if="hasSelectedCard && !debugCardQuery"
                                class="absolute left-3 right-12 top-1/2 -translate-y-1/2 flex items-center gap-2 pointer-events-none z-10"
                            >
                                <img
                                    v-if="pendingCardChange?.card_image"
                                    :src="`${cdnUrl}/${pendingCardChange.card_image.path}/_small/${pendingCardChange.card_image.file_name}`"
                                    :alt="
                                        pendingCardChange.title ||
                                        pendingCardChange.name
                                    "
                                    class="w-8 h-5 rounded-sm object-cover flex-shrink-0"
                                />
                                <img
                                    v-else-if="pendingCardChange?.image_url"
                                    :src="`${cdnUrl}${pendingCardChange.image_url}`"
                                    :alt="
                                        pendingCardChange.title ||
                                        pendingCardChange.name
                                    "
                                    class="w-8 h-5 rounded-sm object-cover flex-shrink-0"
                                />
                                <span
                                    class="text-slate-800 dark:text-slate-100 text-sm truncate flex-1 min-w-0"
                                >
                                    {{
                                        pendingCardChange?.title ||
                                        pendingCardChange?.name ||
                                        pendingCardChange?.label ||
                                        ''
                                    }}
                                </span>
                            </span>

                            <button
                                v-if="debugCardQuery || hasSelectedCard"
                                type="button"
                                class="absolute right-2 top-1/2 -translate-y-1/2 text-slate-600 dark:text-slate-300 w-4 h-4 z-20"
                                @click="$emit('clear')"
                                aria-label="Clear search"
                            >
                                <Icon icon="close" class="w-3 h-3" />
                            </button>

                            <Teleport to="body">
                                <Transition name="fade">
                                    <div
                                        v-if="debugShowDropdown"
                                        class="flex flex-col p-2 overflow-y-auto bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 fixed z-[99999] max-h-[400px]"
                                        :style="`top: ${dropdownTop}px; left: ${dropdownLeft}px; width: ${dropdownWidth}px;`"
                                    >
                                        <div
                                            v-if="
                                                groupedDebugResults.length > 0
                                            "
                                            class="flex flex-col"
                                        >
                                            <div
                                                v-for="(
                                                    group, groupIndex
                                                ) in groupedDebugResults"
                                                :key="'group-' + groupIndex"
                                                class="flex flex-col"
                                            >
                                                <div
                                                    class="px-3 py-2 text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                                                >
                                                    {{ group.bankName }}
                                                </div>

                                                <button
                                                    v-for="(
                                                        card, cardIndex
                                                    ) in group.cards"
                                                    :key="
                                                        'card-' +
                                                        groupIndex +
                                                        '-' +
                                                        cardIndex
                                                    "
                                                    type="button"
                                                    class="flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-700 rounded"
                                                    @click="
                                                        $emit('select', card)
                                                    "
                                                >
                                                    <ImageLoader
                                                        class="flex-shrink-0"
                                                    >
                                                        <template #image>
                                                            <img
                                                                v-if="
                                                                    card?.card_image
                                                                "
                                                                :src="`${cdnUrl}/${card.card_image.path}/_small/${card.card_image.file_name}`"
                                                                :alt="
                                                                    card.title ||
                                                                    card.name ||
                                                                    card.label ||
                                                                    'Card'
                                                                "
                                                                class="w-8 h-5 rounded-sm object-cover"
                                                            />
                                                            <img
                                                                v-else-if="
                                                                    card?.image_url
                                                                "
                                                                :src="`${cdnUrl}${card.image_url}`"
                                                                :alt="
                                                                    card.title ||
                                                                    card.name ||
                                                                    card.label ||
                                                                    'Card'
                                                                "
                                                                class="w-8 h-5 rounded-sm object-cover"
                                                            />
                                                            <img
                                                                v-else-if="
                                                                    card?.card_art_url
                                                                "
                                                                :src="
                                                                    card.card_art_url
                                                                "
                                                                :alt="
                                                                    card.title ||
                                                                    card.name ||
                                                                    card.label ||
                                                                    'Card'
                                                                "
                                                                class="w-8 h-5 rounded-sm object-cover"
                                                            />
                                                        </template>
                                                        <template #error>
                                                            <Icon
                                                                icon="credit-card"
                                                                class="text-slate-600 dark:text-slate-300 w-8 h-5"
                                                            />
                                                        </template>
                                                    </ImageLoader>
                                                    <span
                                                        class="flex-1 text-slate-800 dark:text-slate-100 text-sm font-normal leading-5 truncate"
                                                    >
                                                        {{
                                                            card.title ||
                                                            card.name ||
                                                            card.label ||
                                                            card.issuer ||
                                                            ''
                                                        }}
                                                    </span>
                                                </button>
                                            </div>
                                        </div>
                                        <div
                                            v-else
                                            class="px-3 py-3 text-center text-slate-500 dark:text-slate-400 text-sm"
                                        >
                                            No cards found
                                        </div>
                                    </div>
                                </Transition>
                            </Teleport>
                        </div>
                    </div>

                    <div v-if="changeCard" class="flex items-end gap-2">
                        <button
                            type="button"
                            @click="$emit('cancel')"
                            class="flex items-center justify-center w-12 h-[42px] rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors duration-150"
                            aria-label="Cancel change card"
                        >
                            <Icon
                                icon="close"
                                class="w-5 h-5 text-slate-700 dark:text-slate-300"
                            />
                        </button>
                        <button
                            type="button"
                            @click="$emit('apply')"
                            class="flex items-center justify-center w-12 h-[42px] rounded-md bg-slate-800 dark:bg-slate-600 shadow-sm transition-colors duration-150"
                            :class="
                                hasSelectedCard
                                    ? 'hover:bg-slate-700 dark:hover:bg-slate-500'
                                    : 'opacity-50 cursor-not-allowed'
                            "
                            :disabled="!hasSelectedCard"
                            aria-label="Apply change card"
                        >
                            <Icon icon="check" class="w-5 h-5 text-white" />
                        </button>
                    </div>
                </div>
            </div>
        </Transition>

        <transition
            enter-active-class="transition-opacity duration-150 ease-out"
            enter-from-class="opacity-0"
            leave-active-class="transition-opacity duration-150 ease-out"
            leave-to-class="opacity-0"
        >
            <div
                v-if="changeCard && hasSelectedCard"
                class="text-red-500 text-xs flex items-center gap-2 py-1.5 px-2"
                role="alert"
            >
                <Icon icon="caution" />
                Click the ✓ to apply your new card, or the × to discard it,
                before saving.
            </div>
        </transition>

        <!-- Card Not Listed Section -->
        <div
            v-if="changeCard || !selectedCard?.card"
            class="flex flex-col items-start self-stretch mt-2 px-2"
        >
            <button
                type="button"
                @click="$emit('open-missing-card')"
                class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5 hover:text-slate-600 dark:hover:text-slate-300 transition-colors cursor-pointer"
            >
                Card not listed?
            </button>
        </div>
    </div>
</template>

<script setup>
import Icon from '@/components/ui/icon/icon.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';
import WalletIcon from '@/components/wallet/wallet-icon.vue';

defineProps({
    selectedCard: Object,
    displayedCard: { type: Object, default: null },
    changeCard: Boolean,
    debugCardQuery: String,
    hasSelectedCard: Boolean,
    pendingCardChange: Object,
    cdnUrl: String,
    debugShowDropdown: Boolean,
    dropdownTop: Number,
    dropdownLeft: Number,
    dropdownWidth: Number,
    groupedDebugResults: Array,
});

defineEmits([
    'update:changeCard',
    'update:debugCardQuery',
    'update:debugInputRef',
    'cancel',
    'apply',
    'clear',
    'select',
    'search',
    'focus',
    'open-missing-card',
]);
</script>
